export interface KayipCiroSatiri { gun: string; fire_tutar: number; ciro_tutar: number }

/**
 * 🔴 Oranın İKİ tarafı da aynı pencereden toplanır, SONRA bölünür.
 * Günlük oranların ortalaması alınamaz: fire'sız gün 0, cirosuz gün tanımsız verir ve
 * 990065 bilerek her iki tür günü de döndürür (FULL OUTER — payda %6,2 kaybı bu yüzden yok).
 */
export function kayipCiroOrani(satirlar: KayipCiroSatiri[]) {
  let fire = 0; let ciro = 0;
  for (const s of satirlar) { fire += Number(s.fire_tutar) || 0; ciro += Number(s.ciro_tutar) || 0; }
  return { fire, ciro, oran: ciro > 0 ? (100 * fire) / ciro : null };
}

/** S6 — Σpay / Σpayda. Satır oranlarının ortalaması DEĞİL. */
export function oranTopla<T>(satirlar: T[], pay: keyof T, payda: keyof T): number | null {
  let p = 0; let q = 0;
  for (const s of satirlar) { p += Number(s[pay]) || 0; q += Number(s[payda]) || 0; }
  return q > 0 ? p / q : null;
}

/** S6 — ürün tanesindeki ds'i kategoriye indirger; oran her kategoride Σpay/Σpayda. */
export function kategoriyeTopla<T extends { kategori: string }>(
  satirlar: T[], pay: keyof T, payda: keyof T,
): { kategori: string; pay: number; payda: number; oran: number | null }[] {
  const harita = new Map<string, { pay: number; payda: number }>();
  for (const s of satirlar) {
    const k = harita.get(s.kategori) ?? { pay: 0, payda: 0 };
    k.pay += Number(s[pay]) || 0;
    k.payda += Number(s[payda]) || 0;
    harita.set(s.kategori, k);
  }
  return [...harita.entries()].map(([kategori, v]) => ({
    kategori, pay: v.pay, payda: v.payda, oran: v.payda > 0 ? v.pay / v.payda : null,
  }));
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// S352: ölü stok (990077), stok dönüş süresi (990048) ve stok gün (990047) için saf yardımcılar
// ════════════════════════════════════════════════════════════════════════════════════════════

/** 990077 satırı. `tur`: 'satildi' = daha önce satıldı, satmıyor · 'hic' = geldi, hiç satılmadı. */
export interface OluStokSatiri {
  stok_kod: string; stok_ad: string; kategori: string;
  tur: string; referans_gun: string; gun: number; eldeki: number; deger: number;
}
export type OluTur = 'satildi' | 'hic';
export const OLU_TURLERI: { tur: OluTur; baslik: string }[] = [
  { tur: 'satildi', baslik: 'Daha önce satıldı, satmıyor' },
  { tur: 'hic', baslik: 'Geldi, hiç satılmadı' },
];
export const OLU_KOVALARI = ['30-59 gün', '60-89 gün', '90-179 gün', '180+ gün'] as const;
export type OluKova = (typeof OLU_KOVALARI)[number];

/** Gün sayısı → kova. 30 günün altı ölü stok adayı değildir (ds zaten döndürmez) → null. */
export function oluKovasi(gun: number): OluKova | null {
  if (!(gun >= 30)) return null;
  if (gun < 60) return '30-59 gün';
  if (gun < 90) return '60-89 gün';
  if (gun < 180) return '90-179 gün';
  return '180+ gün';
}

export interface OluKovaOzeti { kova: OluKova; urun: number; deger: number }

/** Bir türün dört kovası (boş kova da 0 ile döner: iki tür yan yana aynı satırlarda dursun). */
export function oluKovaOzeti(satirlar: OluStokSatiri[], tur: OluTur): OluKovaOzeti[] {
  const cikti: OluKovaOzeti[] = OLU_KOVALARI.map((kova) => ({ kova, urun: 0, deger: 0 }));
  for (const s of satirlar) {
    if (s.tur !== tur) continue;
    const kova = oluKovasi(Number(s.gun));
    const hedef = cikti.find((k) => k.kova === kova);
    if (hedef) { hedef.urun += 1; hedef.deger += Number(s.deger) || 0; }
  }
  return cikti;
}

/**
 * Stok kaç günde dönüyor = dönem günü × ortalama stok değeri / dönemde satılan malın maliyeti.
 * Devir hızının (maliyet / ortalama stok) dönem uzunluğundan bağımsız karşılığı. Satış yoksa null.
 */
export function donusGunu(etkinGun: number, satilanMaliyet: number, ortStokDeger: number): number | null {
  if (!(etkinGun > 0) || !(satilanMaliyet > 0) || !(ortStokDeger > 0)) return null;
  return (etkinGun * ortStokDeger) / satilanMaliyet;
}

export interface StokGunSatiri {
  stok_kod: string; stok_ad: string; kategori: string;
  eldeki: number; gunluk_satis: number; kalan_gun: number | null;
}

/**
 * 990047 satırlarını üçe ayırır. Stoğu sıfır ya da eksi görünen ürün "yakında bitecek" DEĞİLDİR
 * (stok kaydı bozuk ya da zaten bitmiş); ayrı listede durur, iki uç listeye karışmaz.
 * `bitecek` kalan güne göre artan, `asiri` azalan sıralıdır; ikisi aynı ürünleri içerir.
 */
export function stokGunAyir(satirlar: StokGunSatiri[]) {
  const eksi = satirlar.filter((s) => !(Number(s.eldeki) > 0));
  const gecerli = satirlar
    .filter((s): s is StokGunSatiri & { kalan_gun: number } => Number(s.eldeki) > 0 && s.kalan_gun !== null)
    .map((s) => ({ ...s, kalan_gun: Number(s.kalan_gun) }));
  const bitecek = [...gecerli].sort((a, b) => a.kalan_gun - b.kalan_gun);
  const asiri = [...gecerli].sort((a, b) => b.kalan_gun - a.kalan_gun);
  return { bitecek, asiri, eksi };
}

/** 990078 satırı: dönemde yalnız `sube`de satılmış ürün. Stok kolonları kayıtlı şube stoğudur. */
export interface SubeFarkSatiri {
  sube: string; stok_kod: string; stok_ad: string; kategori: string;
  satis_miktar: number; satis_tutar: number; sube_stok: number; diger_stok: number;
}
